import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Link,
  alpha
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ApiEndpoint } from '../../types';
import { 
  getIntelligentFields, 
  getHighPriorityFields, 
  getCategoryInfo,
  FieldInfo,
  CategorizedFields 
} from '../../utilities/intelligentFieldSelection';

// Field display configuration constants
const FIELD_DISPLAY_CONFIG = {
  /** Maximum number of array items to display before showing "more" indicator */
  MAX_ARRAY_ITEMS: 5,
  /** Maximum URL length before truncation */
  MAX_URL_LENGTH: 50,
  /** URL preview length when truncated */
  URL_PREVIEW_LENGTH: 47,
  /** Maximum string length before truncation with tooltip */
  MAX_STRING_LENGTH: 200,
  /** String preview length when truncated */
  STRING_PREVIEW_LENGTH: 197,
} as const;

interface IntelligentFieldDisplayProps {
  item: any;
  endpoint?: ApiEndpoint;
  mode?: 'compact' | 'full';
  maxCompactFields?: number;
}

/**
 * Format field value for display based on its data type
 */
const formatFieldValue = (field: FieldInfo): React.ReactNode => {
  const { value, dataType } = field;
  
  if (value === null || value === undefined) {
    return <Typography component="span" color="text.secondary" fontStyle="italic">No value</Typography>;
  }
  
  switch (dataType) {
    case 'boolean':
      return (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          size="small"
          color={value ? 'success' : 'default'}
          variant="filled"
          sx={{
            fontWeight: 500,
            fontSize: '0.8rem',
            height: 28,
            '& .MuiChip-label': {
              px: 1.5
            }
          }}
        />
      );
      
    case 'date':
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return (
            <Typography 
              component="span" 
              sx={{ 
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                backgroundColor: alpha('#f5f5f5', 0.8),
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                border: `1px solid ${alpha('#000', 0.08)}`
              }}
            >
              {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          );
        }
      } catch {
        // Fall through to default
      }
      return <Typography component="span">{String(value)}</Typography>;
      
    case 'url':
      return (
        <Link 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer"
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 0.5,
            wordBreak: 'break-all'
          }}
        >
          {String(value).length > FIELD_DISPLAY_CONFIG.MAX_URL_LENGTH ? 
            `${String(value).substring(0, FIELD_DISPLAY_CONFIG.URL_PREVIEW_LENGTH)}...` : 
            String(value)}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      );
      
    case 'number':
      return (
        <Typography 
          component="span" 
          sx={{ 
            fontFamily: 'monospace', 
            fontWeight: 600,
            fontSize: '0.9rem',
            backgroundColor: alpha('#e3f2fd', 0.6),
            color: '#1976d2',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            border: `1px solid ${alpha('#1976d2', 0.2)}`
          }}
        >
          {typeof value === 'number' ? value.toLocaleString() : String(value)}
        </Typography>
      );
      
    case 'array':
      if (Array.isArray(value) && value.length > 0) {
        const maxItems = FIELD_DISPLAY_CONFIG.MAX_ARRAY_ITEMS;
        return (
          <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {value.slice(0, maxItems).map((item, index) => (
              <Chip 
                key={index}
                label={String(item)} 
                size="small" 
                variant="filled"
                sx={{ 
                  backgroundColor: alpha('#f0f4f8', 0.8),
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  height: 28,
                  border: `1px solid ${alpha('#000', 0.08)}`,
                  '&:hover': {
                    backgroundColor: alpha('#e2e8f0', 0.9),
                  }
                }}
              />
            ))}
            {value.length > maxItems && (
              <Chip 
                label={`+${value.length - maxItems} more`} 
                size="small" 
                variant="filled"
                color="primary"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  height: 28
                }}
              />
            )}
          </Box>
        );
      }
      return <Typography component="span" color="text.secondary" fontStyle="italic">Empty list</Typography>;
      
    case 'object':
      if (typeof value === 'object' && value !== null) {
        // Handle Fulcrum record link fields specially
        if ((value as any).record_id) {
          return (
            <Chip 
              label="Linked Record" 
              size="small" 
              variant="filled"
              color="info"
              sx={{ 
                fontWeight: 500,
                fontSize: '0.8rem',
                height: 28,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          );
        }
        
        // Handle Fulcrum photo fields specially
        if ((value as any).photo_id) {
          return (
            <Chip 
              label="Photo Attachment" 
              size="small" 
              variant="filled"
              color="secondary"
              sx={{ 
                fontWeight: 500,
                fontSize: '0.8rem',
                height: 28,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          );
        }
        
        // Handle Fulcrum choice fields specially
        if ((value as any).choice_values || (value as any).other_values) {
          const choices = [...((value as any).choice_values || []), ...((value as any).other_values || [])];
          if (choices.length > 0) {
            return (
              <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {choices.map((choice: any, index: number) => (
                  <Chip 
                    key={index}
                    label={String(choice)} 
                    size="small" 
                    variant="filled"
                    sx={{ 
                      backgroundColor: alpha('#f0f4f8', 0.8),
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      height: 28,
                      border: `1px solid ${alpha('#000', 0.08)}`,
                      '&:hover': {
                        backgroundColor: alpha('#e2e8f0', 0.9),
                      }
                    }}
                  />
                ))}
              </Box>
            );
          }
        }
        
        // For other objects, show a clean representation
        const keys = Object.keys(value);
        if (keys.length === 0) {
          return <Typography component="span" color="text.secondary" fontStyle="italic">Empty object</Typography>;
        }
        
        return (
          <Chip 
            label={`Object (${keys.length} ${keys.length === 1 ? 'property' : 'properties'})`}
            size="small" 
            variant="outlined"
            sx={{ 
              fontWeight: 500,
              fontSize: '0.8rem',
              height: 28
            }}
          />
        );
      }
      return <Typography component="span" color="text.secondary" fontStyle="italic">No data</Typography>;
      
    default:
      const stringValue = String(value);
      // Truncate very long text values
      if (stringValue.length > FIELD_DISPLAY_CONFIG.MAX_STRING_LENGTH) {
        return (
          <Tooltip title={stringValue} arrow>
            <Typography component="span" sx={{ cursor: 'help' }}>
              {stringValue.substring(0, FIELD_DISPLAY_CONFIG.STRING_PREVIEW_LENGTH)}...
            </Typography>
          </Tooltip>
        );
      }
      return <Typography component="span">{stringValue}</Typography>;
  }
};

/**
 * Render a single field row
 */
const FieldRow: React.FC<{ field: FieldInfo }> = ({ field }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 1, sm: 3 }, // Increased gap
      py: 2, // More padding
      px: 2,
      borderRadius: 2, // More rounded
      
      // Enhanced styling
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      border: `1px solid ${alpha('#000', 0.04)}`,
      
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        borderColor: alpha('#1976d2', 0.2),
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      },
      
      // Smooth transitions
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: 'text.primary',
        minWidth: { sm: '160px' }, // Wider for better alignment
        flex: { sm: '0 0 auto' },
        fontSize: '0.9rem',
        letterSpacing: '0.02em'
      }}
    >
      {field.displayName}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography 
        variant="body2" 
        component="div" 
        sx={{ 
          color: 'text.primary', // Darker text for better readability
          fontSize: '0.9rem',
          lineHeight: 1.5,
          wordBreak: 'break-word' // Handle long values gracefully
        }}
      >
        {formatFieldValue(field)}
      </Typography>
    </Box>
  </Box>
);

/**
 * Render a category section with accordion
 */
const CategorySection: React.FC<{ 
  category: keyof CategorizedFields; 
  fields: FieldInfo[]; 
  defaultExpanded?: boolean;
}> = ({ category, fields, defaultExpanded = false }) => {
  const categoryInfo = getCategoryInfo(category);
  
  if (fields.length === 0) return null;
  
  return (
    <Accordion 
      defaultExpanded={defaultExpanded} 
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: 'transparent',
        '&:before': { display: 'none' },
        mb: 2, // Increased margin
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha('#000', 0.06)}`, // Subtle border
        
        // Enhanced styling
        '&.Mui-expanded': {
          margin: '0 0 16px 0', // Consistent margin when expanded
        }
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon sx={{ color: 'white', fontSize: 16 }} />}
        sx={{ 
          minHeight: 32, // Much smaller height
          px: 0,
          py: 0,
          backgroundColor: '#333333',
          borderRadius: 0,
          
          '&:hover': {
            backgroundColor: '#444444',
          },
          
          // Smooth transitions
          transition: 'background-color 0.2s ease-in-out',
          '& .MuiAccordionSummary-content': { 
            margin: 0,
            alignItems: 'center',
            minHeight: 32 // Match the minHeight
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: 'white',
            mr: 0.5, // Reduced margin
            '&.Mui-expanded': {
              transform: 'rotate(180deg)'
            }
          }
        }}
      >
        <Typography 
          variant="caption" // Smaller variant
          sx={{ 
            fontWeight: 500, // Reduced weight
            color: 'white',
            fontSize: '0.75rem', // Smaller font
            letterSpacing: '0.02em', // Reduced letter spacing
            textTransform: 'uppercase',
            px: 1.5, // Reduced padding
            py: 0.5,
            backgroundColor: '#333333',
            borderRadius: 0,
            margin: 0,
            opacity: 0.9 // Slightly transparent to be less prominent
          }}
        >
          {categoryInfo.label}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ 
        pt: 1, 
        pb: 3, 
        px: 3, // Increased padding
        backgroundColor: alpha('#f8f9fa', 0.3), // Very subtle background
        borderTop: `1px solid ${alpha('#000', 0.04)}`, // Subtle separator
      }}>
        <Stack spacing={1.5}> {/* Increased spacing for better readability */}
          {fields.map((field) => (
            <FieldRow key={field.key} field={field} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * Main intelligent field display component
 */
const IntelligentFieldDisplay: React.FC<IntelligentFieldDisplayProps> = ({
  item,
  endpoint,
  mode = 'full',
  maxCompactFields = 6
}) => {
  const [showAll, setShowAll] = useState(false);
  
  if (mode === 'compact') {
    const highPriorityFields = getHighPriorityFields(item, endpoint);
    
    if (highPriorityFields.length === 0) {
      return null;
    }
    
    const displayFields = showAll ? highPriorityFields : highPriorityFields.slice(0, maxCompactFields);
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Key Details
        </Typography>
        <Stack spacing={0}>
          {displayFields.map((field) => (
            <FieldRow key={field.key} field={field} />
          ))}
        </Stack>
        {highPriorityFields.length > maxCompactFields && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'primary.main', 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Show Less' : `Show ${highPriorityFields.length - maxCompactFields} More`}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
  
  // Full mode - show categorized fields
  const categorizedFields = getIntelligentFields(item, endpoint);
  
  // Check if we have any fields to display
  const totalFields = Object.values(categorizedFields).reduce((sum, fields) => sum + fields.length, 0);
  
  if (totalFields === 0) {
    return null;
  }
  
  // Get smart expansion state based on content
  const smartExpansion = getSmartExpansionState(item, categorizedFields);
  
  return (
    <Box>
      <Stack spacing={2}> {/* Increased spacing for better readability */}
        {/* Details - always expanded by default */}
        <CategorySection 
          category="details" 
          fields={categorizedFields.details} 
          defaultExpanded={true}
        />
        
        {/* Temporal fields - smart expansion for context */}
        <CategorySection 
          category="temporal" 
          fields={categorizedFields.temporal}
          defaultExpanded={smartExpansion.temporal}
        />
        
        {/* Technical fields - collapsed by default */}
        <CategorySection 
          category="technical" 
          fields={categorizedFields.technical}
          defaultExpanded={smartExpansion.technical}
        />
        
        {/* Metadata - collapsed by default */}
        <CategorySection 
          category="metadata" 
          fields={categorizedFields.metadata}
          defaultExpanded={smartExpansion.metadata}
        />
      </Stack>
    </Box>
  );
};

// Smart expansion configuration constants
const SMART_EXPANSION_CONFIG = {
  /** Minimum character length for a meaningful description */
  MIN_MEANINGFUL_DESCRIPTION_LENGTH: 10,
  /** Maximum descriptive fields before considering content "sparse" */
  MAX_SPARSE_DESCRIPTIVE_FIELDS: 2,
  /** Maximum temporal fields before expanding technical section */
  MAX_SPARSE_TEMPORAL_FIELDS: 1,
} as const;

/**
 * Smart expansion logic: determine which sections should be expanded by default
 * This provides an intelligent, adaptive UX that works across different datasets and customers
 */
const getSmartExpansionState = (item: any, categorizedFields: CategorizedFields): Record<string, boolean> => {
  // Details section is always expanded, so we only need to determine other sections
  const expansionState = {
    details: true, // Always expanded
    temporal: false,
    technical: false,
    metadata: false
  };

  // Check multiple sources for meaningful description content
  const hasMainDescription = item?.description && 
                            typeof item.description === 'string' && 
                            item.description.trim().length > SMART_EXPANSION_CONFIG.MIN_MEANINGFUL_DESCRIPTION_LENGTH;

  // Also check Fulcrum form values for description-like fields
  const hasFulcrumDescription = item?.fulcrum_data?.form_values && 
                               Object.entries(item.fulcrum_data.form_values).some(([key, value]) => {
                                 const isDescField = key.toLowerCase().includes('description') || 
                                                   key.toLowerCase().includes('notes') || 
                                                   key.toLowerCase().includes('comment') ||
                                                   key.toLowerCase().includes('observation');
                                 return isDescField && 
                                        typeof value === 'string' && 
                                        value.trim().length > SMART_EXPANSION_CONFIG.MIN_MEANINGFUL_DESCRIPTION_LENGTH;
                               });

  const hasAnyDescription = hasMainDescription || hasFulcrumDescription;

  // Smart expansion based on content availability
  if (!hasAnyDescription) {
    // No meaningful description found - expand sections to provide context
    
    // If details section is sparse, also expand temporal for additional context
    if (categorizedFields.details.length <= SMART_EXPANSION_CONFIG.MAX_SPARSE_DESCRIPTIVE_FIELDS && 
        categorizedFields.temporal.length > 0) {
      expansionState.temporal = true;
    }
    
    // For very sparse items, also show technical details
    if (categorizedFields.details.length === 0 && 
        categorizedFields.temporal.length <= SMART_EXPANSION_CONFIG.MAX_SPARSE_TEMPORAL_FIELDS && 
        categorizedFields.technical.length > 0) {
      expansionState.technical = true;
    }
  }

  return expansionState;
};

export default IntelligentFieldDisplay;